import ProductThumb from "@/components/ProductThumb";
import { useAuth } from "@/context/AuthContext";
import { channelLabels } from "@/lib/sectorVisibility";
import { ChannelBadge } from "@/components/ChannelBadge";
import { fmtTL, ExpiryBadge, StockBadge } from "@/components/StatusBadge";
import { isFreshProduce } from "@/lib/produce";
import { TrendingUp, TrendingDown, Store, Truck, Sparkles, Check } from "lucide-react";

export default function ProductCard({ product, onClick, lens, selectable = false, selected = false, onToggleSelect, campaignActive = false }) {
  const { can, activeSectorObj } = useAuth();
  const chLabels = channelLabels(activeSectorObj);
  const showPurchase = can("purchase.read");
  const showMarket = can("price.read");
  const p = product;
  const hist = (p.purchase_history || []).slice(-3);
  const sp = p.sales_prices || {};
  const ma = p.market_avg || {};
  const isProduce = isFreshProduce(p.category);
  const priceFresh = ma.updated_at && String(ma.updated_at).slice(0, 10) === new Date().toISOString().slice(0, 10);
  const activeLens = lens || p.channel_type || "market";
  const isToptan = activeLens === "toptan" || activeLens === "rota";
  const priceByLens = { toptan: sp.wholesale_pickup, rota: sp.wholesale_delivered, eticaret: sp.market_web, pazaryeri: sp.marketplace, market: sp.market_retail };
  const our = priceByLens[activeLens] != null ? priceByLens[activeLens] : sp.market_retail;
  const marketAvg = ma.avg;
  const cheaper = showMarket && marketAvg != null && our > 0 && our < marketAvg;
  const rawMargin = showPurchase && Number.isFinite(our) && our > 0 && Number.isFinite(p.last_cost) && p.last_cost > 0
    ? (our - p.last_cost) / our * 100 : null;
  const margin = Number.isFinite(rawMargin) ? Number(rawMargin.toFixed(1)) : null;
  const isLoss = rawMargin < 0;
  const marginLabel = margin === 0 && rawMargin !== 0 ? (isLoss ? "Zarar <%0,1" : "Kâr <%0,1") : `%${margin}`;

  return (
    <button
      data-testid={`product-card-${p.id}`}
      onClick={selectable ? onToggleSelect : onClick}
      className={`group flex w-full flex-col overflow-hidden rounded-lg bg-white text-left transition-all hover:shadow-md focus:outline-none ${selected ? "ring-2 ring-red-500" : "ring-1 ring-inset ring-slate-200 hover:ring-brand-green/40 focus:ring-2 focus:ring-brand-green"}`}
    >
      <div className="relative h-24 w-full overflow-hidden">
        {selectable && (
          <div data-testid={`product-select-${p.id}`} className={`absolute left-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-md border-2 ${selected ? "border-red-500 bg-red-500 text-white" : "border-white bg-black/40 text-transparent"}`}>
            <Check className="h-4 w-4" />
          </div>
        )}
        <ProductThumb product={p} className="h-full w-full transition-transform duration-300 group-hover:scale-105 text-2xl" />
        {p.opportunity?.found && (
          <div data-testid={`opp-ribbon-${p.id}`} className="absolute left-0 top-2 flex items-center gap-1 rounded-r-full bg-emerald-600 py-0.5 pl-2 pr-2.5 text-[10px] font-bold text-white shadow">
            <Sparkles className="h-3 w-3" /> FIRSAT %{p.opportunity.saving_pct}
          </div>
        )}
        {campaignActive && (
          <div data-testid={`campaign-ribbon-${p.id}`} className={`absolute left-0 ${p.opportunity?.found ? "top-9" : "top-2"} rounded-r-full bg-brand-orange py-0.5 pl-2 pr-2.5 text-[10px] font-bold text-white shadow`}>KAMPANYALI</div>
        )}
        <div className="absolute right-2 top-2">
          <StockBadge qty={p.stock_on_hand} unit={p.base_unit} testid={`stock-${p.id}`} />
        </div>
        <div className="absolute left-2 bottom-2">
          <ChannelBadge channel={activeLens} upper className="rounded-sm bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-white backdrop-blur" />
        </div>
      </div>

      <div className="flex flex-1 flex-col p-3">
        <div className="mb-1 flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 text-sm font-semibold text-slate-900">{p.name}</h3>
          {showPurchase && margin != null && (
            <span className={`inline-flex shrink-0 items-center gap-0.5 text-[11px] font-bold tabular ${!isLoss ? "text-emerald-600" : "text-red-600"}`}>
              {!isLoss ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{marginLabel}
            </span>
          )}
        </div>
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-[10px] text-slate-400">{p.sku}</span>
          {p.is_standard && <span data-testid={`std-badge-${p.id}`} title="Standart ürün — kalıcı; Standart Kilit açıkken silinemez, sıfırlama sonrası geri gelir" className="inline-flex items-center rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-700 ring-1 ring-inset ring-sky-200">Standart</span>}
          {p.expiry_date && <ExpiryBadge dateStr={p.expiry_date} testid={`expiry-${p.id}`} />}
        </div>

        {showPurchase && (
          <div className="mb-2 rounded-sm bg-slate-50 p-2">
            <p className="overline mb-1">Son 3 Alış Fiyatı</p>
            <div className="flex items-center gap-1">
              {hist.length === 0 && <span className="text-[11px] text-slate-400">Kayıt yok</span>}
              {hist.map((h, i) => (
                <span key={i} className={`tabular rounded-sm px-1 py-0.5 text-[11px] font-medium ${i === hist.length - 1 ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200"}`}>
                  {fmtTL(h.unit_cost)}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className={`mt-auto grid gap-2 ${showMarket ? "grid-cols-2" : "grid-cols-1"}`}>
          {showMarket && (
            <div data-testid={`market-box-${p.id}`} className={`rounded-sm border p-2 ${isProduce && priceFresh ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`}>
              <p className="overline mb-0.5 flex items-center gap-1">
                {isProduce ? "Bugünün Fiyatı" : "Piyasa Ort."}
                {isProduce && marketAvg != null && (
                  <span title={priceFresh ? "Bugün araştırıldı (çok kaynaklı perakende)" : "Güncel değil — 'Manav Bugünün Fiyatı'nı çalıştırın"}
                    className={`inline-block h-1.5 w-1.5 rounded-full ${priceFresh ? "bg-emerald-500" : "bg-amber-400"}`} />
                )}
              </p>
              <p className="tabular text-sm font-bold text-slate-700">
                {marketAvg != null
                  ? <>{fmtTL(marketAvg)}{isProduce && p.base_unit ? <span className="ml-0.5 text-[10px] font-normal text-slate-400">/{String(p.base_unit).toLowerCase()}</span> : null}</>
                  : "—"}
              </p>
              {isProduce && marketAvg != null && (ma.count || ma.confidence) ? (
                <p className="mt-0.5 flex items-center gap-1 text-[9px] text-slate-400">
                  {ma.count ? <span>{ma.count} kaynak</span> : null}
                  {ma.confidence ? (
                    <span className={`rounded px-1 font-semibold ${ma.confidence === "high" ? "bg-emerald-100 text-emerald-700" : ma.confidence === "medium" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                      {ma.confidence === "high" ? "güvenli" : ma.confidence === "medium" ? "orta güven" : "düşük · incele"}
                    </span>
                  ) : <span>araştırıldı</span>}
                </p>
              ) : null}
            </div>
          )}
          <div className={`rounded-sm p-2 ${our > 0 ? (cheaper ? "bg-emerald-50 ring-1 ring-inset ring-emerald-200" : "bg-orange-50 ring-1 ring-inset ring-orange-200") : "bg-red-50 ring-1 ring-inset ring-red-200"}`}>
            <p className="overline mb-0.5 flex items-center gap-1">
              {isToptan ? <Truck className="h-3 w-3" /> : <Store className="h-3 w-3" />} {isToptan ? chLabels.toptan : chLabels.market} Satış
            </p>
            <p className="tabular text-sm font-bold text-slate-900">{our > 0 ? fmtTL(our) : <span className="text-red-600" data-testid={`card-unpriced-${p.id}`}>Fiyatsız</span>}</p>
          </div>
        </div>
      </div>
    </button>
  );
}
